/*
  "Notify me when sold out" — Klaviyo back-in-stock signup.

  Two custom elements:
    <notify-me-modal>   one per page, rendered from layout/theme.liquid.
                        Extends the theme's ModalDialog (assets/global.js) for
                        Escape / backdrop click / focus trap / scroll lock.
    <notify-me-trigger> wraps the "Notify me" button inside .product__buy-buttons.
                        Opens the modal and keeps the variant in sync.

  Loaded after global.js with defer, so ModalDialog is defined by the time this
  runs. Do not move it above global.js.
*/

const NOTIFY_ME_API_REVISION = "2026-07-15";
const NOTIFY_ME_BIS_URL = "https://a.klaviyo.com/client/back-in-stock-subscriptions/";
const NOTIFY_ME_SUBSCRIPTIONS_URL = "https://a.klaviyo.com/client/subscriptions/";

if (!customElements.get("notify-me-modal")) {
  customElements.define(
    "notify-me-modal",
    class NotifyMeModal extends ModalDialog {
      constructor() {
        super();

        this.form = this.querySelector(".notify-me-form");
        this.emailInput = this.querySelector('input[name="email"]');
        this.consentInput = this.querySelector('input[name="marketing"]');
        this.submitButton = this.querySelector(".notify-me-form__submit");
        this.spinner = this.querySelector(".loading-overlay__spinner");
        this.messageElement = this.querySelector(".notify-me-modal__message");
        this.productElement = this.querySelector("[data-notify-me-product]");
        this.productImage = this.querySelector("[data-notify-me-image]");
        this.productTitle = this.querySelector("[data-notify-me-title]");
        this.productVariant = this.querySelector("[data-notify-me-variant]");
        this.productVariantText = this.querySelector(
          "[data-notify-me-variant-text]"
        );
        this.productSwatch = this.querySelector("[data-notify-me-swatch]");

        this.form.addEventListener("submit", this.onSubmit.bind(this));
      }

      /*
        Deliberately no disconnectedCallback here.

        ModalDialog.connectedCallback does document.body.appendChild(this), which
        MOVES this element — firing disconnectedCallback and then connectedCallback
        again. A cleanup hook would tear the submit listener straight back off on
        first upgrade. There is nothing to leak either way: the listener is on a
        descendant of this element, so it dies with it. Only listeners on document
        or window need removing (see <notify-me-trigger> below).
      */

      get klaviyoKey() {
        return this.dataset.klaviyoKey;
      }

      get listId() {
        return this.dataset.klaviyoListId;
      }

      show(opener) {
        this.variantId = opener.dataset.variantId;
        this.reset();

        this.renderProduct(opener.dataset);

        super.show(opener);
        this.emailInput.focus();
      }

      // Image left, name + variant pill right. Every field is optional: a product
      // with no image, a single-variant product, and a bundle each drop a piece.
      renderProduct(data) {
        const title = data.productTitle;

        if (!title) {
          this.productElement.hidden = true;
          return;
        }

        this.productTitle.textContent = title;

        if (data.image) {
          this.productImage.src = data.image;
          this.productImage.alt = title;
          this.productImage.hidden = false;
        } else {
          this.productImage.removeAttribute("src");
          this.productImage.hidden = true;
        }

        // Blank for single-variant products ("Default Title") and for bundles,
        // whose variant title is every component at once rather than one choice.
        if (data.variantTitle) {
          this.productVariantText.textContent = data.variantTitle;
          this.productVariant.hidden = false;

          // Swatch value comes from settings.custom_colors via swatch-color.liquid
          // — a hex, an rgb(), or a url(). Blank when swatches are off or the
          // value has no colour, in which case the pill is just text.
          if (data.variantColor) {
            this.productSwatch.style.background = data.variantColor;
            this.productSwatch.hidden = false;
          } else {
            this.productSwatch.style.background = "";
            this.productSwatch.hidden = true;
          }
        } else {
          this.productVariantText.textContent = "";
          this.productVariant.hidden = true;
        }

        this.productElement.hidden = false;
      }

      // Clears whatever the previous open left behind — a success state would
      // otherwise persist into the next product the shopper clicks.
      reset() {
        this.classList.remove("notify-me-modal--sent");
        this.messageElement.hidden = true;
        this.messageElement.textContent = "";
        this.messageElement.classList.remove("notify-me-modal__message--error");
        this.form.reset();
        this.setLoading(false);
      }

      setLoading(isLoading) {
        this.submitButton.classList.toggle("loading", isLoading);
        this.spinner?.classList.toggle("hidden", !isLoading);

        if (isLoading) {
          this.submitButton.setAttribute("aria-disabled", "true");
        } else {
          this.submitButton.removeAttribute("aria-disabled");
        }
      }

      showMessage(text, isError) {
        this.messageElement.textContent = text;
        this.messageElement.classList.toggle(
          "notify-me-modal__message--error",
          Boolean(isError)
        );
        this.messageElement.hidden = false;
      }

      async onSubmit(event) {
        event.preventDefault();

        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        const email = this.emailInput.value.trim();

        // Browser-native email validation — no second regex to keep in sync.
        if (!email || !this.emailInput.checkValidity()) {
          this.emailInput.focus();
          this.showMessage(this.dataset.errorMessage, true);
          return;
        }

        if (!this.klaviyoKey || !this.variantId) {
          this.showMessage(this.dataset.errorMessage, true);
          return;
        }

        this.setLoading(true);
        this.messageElement.hidden = true;

        try {
          await this.subscribeBackInStock(email);
        } catch (error) {
          console.error("[notify-me]", error);
          this.setLoading(false);
          this.showMessage(this.dataset.errorMessage, true);
          return;
        }

        // Marketing opt-in is a separate, best-effort call. It must never turn a
        // successful back-in-stock signup — the thing the shopper actually asked
        // for — into an error.
        if (this.consentInput?.checked && this.listId) {
          try {
            await this.subscribeToList(email);
          } catch (error) {
            console.error("[notify-me] marketing opt-in failed", error);
          }
        }

        this.setLoading(false);
        this.classList.add("notify-me-modal--sent");
        this.showMessage(this.dataset.successMessage, false);
      }

      async subscribeBackInStock(email) {
        const response = await fetch(
          `${NOTIFY_ME_BIS_URL}?company_id=${encodeURIComponent(this.klaviyoKey)}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              revision: NOTIFY_ME_API_REVISION,
            },
            body: JSON.stringify({
              data: {
                type: "back-in-stock-subscription",
                attributes: {
                  profile: {
                    data: { type: "profile", attributes: { email: email } },
                  },
                  channels: ["EMAIL"],
                },
                relationships: {
                  variant: {
                    data: {
                      type: "catalog-variant",
                      // Klaviyo's Shopify catalog id format. "$default" is the
                      // default catalog — it is a literal, not a placeholder.
                      id: `$shopify:::$default:::${this.variantId}`,
                    },
                  },
                },
              },
            }),
          }
        );

        // Success is 202 with an empty body — do not try to parse it as JSON.
        if (response.ok) return;

        throw new Error(await this.describeError(response));
      }

      async subscribeToList(email) {
        const response = await fetch(
          `${NOTIFY_ME_SUBSCRIPTIONS_URL}?company_id=${encodeURIComponent(
            this.klaviyoKey
          )}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              revision: NOTIFY_ME_API_REVISION,
            },
            body: JSON.stringify({
              data: {
                type: "subscription",
                attributes: {
                  custom_source: "Back in stock modal",
                  profile: {
                    data: {
                      type: "profile",
                      attributes: {
                        email: email,
                        subscriptions: {
                          email: { marketing: { consent: "SUBSCRIBED" } },
                        },
                      },
                    },
                  },
                },
                relationships: {
                  list: { data: { type: "list", id: this.listId } },
                },
              },
            }),
          }
        );

        if (response.ok) return;

        throw new Error(await this.describeError(response));
      }

      // Klaviyo returns { errors: [{ status, code, detail }] }. Surfaced to the
      // console only — shoppers see the merchant's error setting either way, but
      // `variant_not_found` is the one worth recognising: it almost always means
      // the Klaviyo catalog has not synced, not that anything is wrong here.
      async describeError(response) {
        let detail = "";

        try {
          const body = await response.json();
          detail = body?.errors?.map((e) => `${e.code}: ${e.detail}`).join("; ");
        } catch (error) {
          detail = response.statusText;
        }

        return `Klaviyo responded ${response.status} — ${detail}`;
      }
    }
  );
}

if (!customElements.get("notify-me-trigger")) {
  customElements.define(
    "notify-me-trigger",
    class NotifyMeTrigger extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector("button");
        this.buyButtons = this.closest(".product__buy-buttons");
        this.swatchColors = this.parseSwatchColors();
        this.variantData = this.parseJSONScript("[data-notify-me-variants]");

        this.onClick = this.onClick.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onWatchedChange = this.onWatchedChange.bind(this);

        this.button?.addEventListener("click", this.onClick);
        document.addEventListener("variant:change", this.onVariantChange);

        // Watch mode: surfaces that swap product/variant with their own JS rather
        // than a Dawn variant picker (subscription landing).
        if (this.dataset.watch) {
          this.scope = this.closest(".shopify-section") || document;
          this.scope.addEventListener("change", this.onWatchedChange);
          this.syncFromWatched();
        }
      }

      disconnectedCallback() {
        this.button?.removeEventListener("click", this.onClick);
        document.removeEventListener("variant:change", this.onVariantChange);
        this.scope?.removeEventListener("change", this.onWatchedChange);
      }

      onClick() {
        const modal = document.getElementById("NotifyMeModal");
        if (modal) modal.show(this.button);
      }

      // { "<variant id>": "<css colour>" }, rendered by Liquid because swatch data
      // is not in the variant JSON. Absent on bundles and single-variant products,
      // which show no pill at all.
      parseSwatchColors() {
        return this.parseJSONScript("[data-notify-me-colors]");
      }

      parseJSONScript(selector) {
        const source = this.querySelector(selector);
        if (!source) return {};

        try {
          return JSON.parse(source.textContent);
        } catch (error) {
          console.error("[notify-me] could not parse", selector, error);
          return {};
        }
      }

      /*
        Watch mode. The radios carry data-variant-id and are `disabled` when
        unavailable, so the only reachable sold-out case is a product whose
        variants are all unavailable — which is exactly when this should show.
      */
      onWatchedChange(event) {
        if (!event.target.matches(this.dataset.watch)) return;
        this.applyWatched(event.target.value);
      }

      syncFromWatched() {
        const checked = this.scope.querySelector(`${this.dataset.watch}:checked`);
        if (checked) this.applyWatched(checked.value);
      }

      applyWatched(variantId) {
        const info = this.variantData[variantId];
        if (!info || !this.button) return;

        const data = this.button.dataset;
        data.variantId = variantId;
        data.productTitle = info.productTitle || "";
        data.image = info.image || info.productImage || "";
        data.productImage = info.productImage || "";
        data.variantTitle = info.showPill ? info.variantTitle || "" : "";
        data.variantColor = info.showPill ? info.color || "" : "";

        const soldOut = info.available === false;
        this.classList.toggle("notify-me-trigger--visible", soldOut);

        // No CSS parent drives the swap on these surfaces, so hide the buy
        // controls directly — but only in "replace" placement.
        const hideTarget = this.dataset.hideTarget
          ? this.scope.querySelector(this.dataset.hideTarget)
          : null;

        hideTarget?.classList.toggle(
          "notify-me-hidden",
          soldOut && this.dataset.placement !== "below"
        );
      }

      /*
        global.js dispatches this on `document` at the end of onVariantChange,
        synchronously — before the renderProductInfo fetch resolves — so the swap
        happens with no flash of the disabled "Sold Out" button.

        Why this and not pubsub: PUB_SUB_EVENTS.variantChange exists in
        constants.js but global.js never publishes it. This CustomEvent is the
        only variant signal available.

        Why we have to react at all: renderProductInfo re-renders price, sku,
        inventory, colour name and media — never buy_buttons. Availability reaches
        the button solely through toggleAddButton, which mutates textContent on
        the existing element. A Liquid-only notify button would be correct on load
        and wrong on every variant switch after that.

        Section matching survives quick-add: preventDuplicatedIDs does a raw
        innerHTML.replaceAll(sectionId, `quickadd-${sectionId}`), which rewrites
        data-section on the variant-selects element and on this one alike.
      */
      onVariantChange(event) {
        const detail = event.detail;
        if (!detail || detail.sectionId !== this.dataset.section) return;

        const variant = detail.variant;
        if (!variant || !this.button) return;

        const data = this.button.dataset;

        data.variantId = variant.id;

        /*
          public_title is null on single-variant products, where `title` is the
          literal "Default Title" — so it is exactly the right field to read.
          data-show-variant-pill is decided once in Liquid and additionally
          suppresses the pill on bundles.
        */
        const showPill = data.showVariantPill === "true";
        data.variantTitle = showPill ? variant.public_title || "" : "";
        data.variantColor = showPill ? this.swatchColors[variant.id] || "" : "";

        // Bundle variants carry featured_image: null, so the product image has
        // to be the fallback or the summary would lose its image mid-session.
        const variantImage = variant.featured_image?.src;
        data.image = variantImage
          ? NotifyMeTrigger.sizedImage(variantImage)
          : data.productImage || "";

        this.buyButtons?.classList.toggle(
          "product__buy-buttons--notify",
          !variant.available
        );
      }

      // Variant JSON gives a full-size CDN URL; Liquid-rendered images already go
      // through image_url: width: 200. Match them so the swap isn't a size jump.
      static sizedImage(src) {
        try {
          const url = new URL(src, window.location.origin);
          url.searchParams.set("width", "200");
          return url.href;
        } catch (error) {
          return src;
        }
      }
    }
  );
}
